import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { FC, PropsWithChildren, useCallback, useState } from "react";
import { useCopyToClipboard } from "react-use";

import { useGetCurrentUrl } from "../../../../hooks/use-get-current-url";
import { Icon } from "../../../icon";
import { getFacebookShareLink, getLinkedinShareLink } from "./share-utils";

const variants = {
  collapsed: {
    y: 0,
  },
  animate: (index: number) => ({
    y: `calc((100% + 12px) * ${index + 1} * -1)`,
  }),
};

interface SideCtaInnerShareMobileButtonProps {
  isOpen: boolean;
  index: number;
  name: string;
}

const SideCtaInnerShareMobileButton: FC<PropsWithChildren<SideCtaInnerShareMobileButtonProps>> = ({
  isOpen,
  index,
  name,
  children,
}) => {
  return (
    <AnimatePresence initial={false} custom={index} mode="popLayout">
      {isOpen && (
        <motion.div
          key={name}
          custom={index}
          variants={variants}
          initial="collapsed"
          animate="animate"
          exit="collapsed"
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            zIndex: 40 - index * 10,
          }}
          className="absolute top-0 left-0"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const SideCtaInnerShareMobile: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUrlCopied, setIsUrlCopied] = useState(false);

  const [, copy] = useCopyToClipboard();
  const url = useGetCurrentUrl();
  const handleUrlCopy = useCallback(() => {
    if (url) {
      copy(url);
      setIsUrlCopied(true);
      setTimeout(() => setIsUrlCopied(false), 1000);
    }
  }, [setIsUrlCopied, copy, url]);

  return !url ? null : (
    <div className="relative overflow-visible flex-col-reverse flex gap-3 text-white">
      <div
        className="overflow-hidden rounded-full p-3 cursor-pointer bg-dark z-50 border-primary border-2"
        onClick={() => {
          setIsOpen(!isOpen);
          setIsUrlCopied(false);
        }}
      >
        <Icon icon={isOpen ? "close" : "share"} size={20} className="w-6 h-6" />
      </div>
      <SideCtaInnerShareMobileButton isOpen={isOpen} index={0} name="facebook">
        <a href={getFacebookShareLink(url)} target="_blank" rel="noreferrer">
          <div className="overflow-hidden rounded-full p-3 cursor-pointer bg-dark border-primary border-2">
            <Icon icon="facebook" size={20} className="w-6 h-6" />
          </div>
        </a>
      </SideCtaInnerShareMobileButton>

      <SideCtaInnerShareMobileButton isOpen={isOpen} index={1} name="linkedin">
        <a href={getLinkedinShareLink(url)} target="_blank" rel="noreferrer">
          <div className="overflow-hidden rounded-full p-3 cursor-pointer bg-dark border-primary border-2">
            <Icon icon="linkedin" size={20} className="w-6 h-6" />
          </div>
        </a>
      </SideCtaInnerShareMobileButton>

      <SideCtaInnerShareMobileButton isOpen={isOpen} index={2} name="url">
        <div
          className={clsx([
            "overflow-hidden rounded-full p-3 cursor-pointer border-2",
            isUrlCopied ? "bg-primary border-dark" : "bg-dark border-primary",
          ])}
          onClick={handleUrlCopy}
        >
          <Icon icon={isUrlCopied ? "check" : "link"} size={20} className="w-6 h-6" />
        </div>
      </SideCtaInnerShareMobileButton>
    </div>
  );
};
